import type { BoardData, Meta, Mix } from '../model/schema.js';
import { lanePorts } from '../trace/ports.js';

/** One stage-box output a mix plays on (U77). */
export interface MixOutput {
  /** "SB1/5" (box label / 1-based output) or the informal text. */
  label: string;
  /** "5" – the output number shown on the plot (the text itself if it has none). */
  number: string;
  /** From a drawn cable (else from `Mix.outputs`). */
  formal: boolean;
  /** The box output port when known. */
  port?: { itemId: string; portId: string };
}

/** Outputs a mix needs: mono one, stereo two (L / R). */
export function outputsNeeded(mix: Pick<Mix, 'stereo'>): number {
  return mix.stereo ? 2 : 1;
}

/** `SB1/5`, `SB1:5`, `SB1 5` → that box's 5th plain output (case-insensitive label). */
export function resolveOutput(
  data: BoardData,
  text: string,
): { itemId: string; portId: string } | undefined {
  const m = /^(.+?)\s*[:/ ]\s*(\d+)$/.exec(text.trim());
  if (m === null) return undefined;
  const label = m[1]!.toLowerCase();
  const index = Number(m[2]) - 1;
  for (const item of Object.values(data.items)) {
    if (item.label.toLowerCase() !== label) continue;
    const out = lanePorts(item).outputs[index];
    if (out !== undefined) return { itemId: item.id, portId: out.id };
  }
  return undefined;
}

/**
 * Output texts from a cell: "SB1/5, SB1/6", "SB1/5-6" (a range on one box) or "SB1/5 SB1/6";
 * empty parts are dropped, at most 8.
 */
export function parseOutputList(text: string): string[] {
  const out: string[] = [];
  for (const part of text.split(/[,;]+|\s{2,}|\s+(?=\S+[:/]\d)/)) {
    const p = part.trim();
    if (p === '') continue;
    const range = /^(.+?[:/ ]\s*)(\d+)\s*-\s*(\d+)$/.exec(p);
    if (range) {
      const a = Number(range[2]);
      const b = Number(range[3]);
      if (b >= a && b - a < 8) {
        for (let n = a; n <= b; n += 1) out.push(`${range[1]!.trim()}${n}`);
        continue;
      }
    }
    out.push(p);
  }
  return [...new Set(out)].slice(0, 8);
}

function numberOf(text: string): string {
  return /(\d+)\s*$/.exec(text)?.[1] ?? text;
}

/** Cables from a box output into `itemId` (stage box, sub-core, console …), in port order. */
function cabledOutputs(data: BoardData, itemId: string): MixOutput[] {
  const out: MixOutput[] = [];
  for (const c of Object.values(data.connections)) {
    if (c.to.itemId !== itemId) continue;
    const box = data.items[c.from.itemId];
    if (box === undefined) continue;
    const index = lanePorts(box).outputs.findIndex((p) => p.id === c.from.portId);
    if (index < 0 || box.kind === 'iem' || box.kind === 'wedge') continue;
    const n = String(index + 1);
    out.push({
      label: `${box.label || box.model || box.kind}/${n}`,
      number: n,
      formal: true,
      port: { itemId: box.id, portId: c.from.portId },
    });
  }
  return out.sort((a, b) => Number(a.number) - Number(b.number));
}

/** The mix's informal outputs (`Mix.outputs`), resolved to box ports where possible. */
export function informalOutputs(data: BoardData, mix: Pick<Mix, 'outputs'>): MixOutput[] {
  return (mix.outputs ?? []).map((text) => {
    const port = resolveOutput(data, text);
    const o: MixOutput = { label: text, number: numberOf(text), formal: false };
    if (port !== undefined) o.port = port;
    return o;
  });
}

/**
 * Outputs of a mix (U77): drawn cables into the monitors it feeds win, else `Mix.outputs`.
 * Duplicates (several wedges on one output) are listed once.
 */
export function mixOutputs(data: BoardData, mix: Mix): MixOutput[] {
  const seen = new Set<string>();
  const formal = mix.outputItemIds
    .flatMap((id) => cabledOutputs(data, id))
    .filter((o) => {
      const key = `${o.port!.itemId}:${o.port!.portId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  return formal.length > 0 ? formal : informalOutputs(data, mix);
}

/**
 * Output badges per monitor (U77): "5" or "5/6", with the full labels as tooltip. A monitor with
 * its own cables shows those, else the outputs of the mix(es) feeding it.
 */
export function monitorOutputBadges(data: BoardData): Map<string, { text: string; title: string }> {
  const by = new Map<string, { text: string; title: string }>();
  const mixes = Object.values(data.mixes).sort((a, b) => a.number - b.number);
  for (const mix of mixes) {
    const shared = informalOutputs(data, mix);
    for (const id of mix.outputItemIds) {
      if (data.items[id] === undefined || by.has(id)) continue;
      const own = cabledOutputs(data, id);
      const outs = own.length > 0 ? own : shared;
      if (outs.length === 0) continue;
      by.set(id, {
        text: outs.map((o) => o.number).join('/'),
        title: outs.map((o) => o.label).join(', '),
      });
    }
  }
  return by;
}

/** Whether monitors show their stage-box outputs (U77, default on). */
export function outputBadgesOn(meta: Pick<Meta, 'outputBadges'>): boolean {
  return meta.outputBadges !== false;
}

/** Bus number text on monitors (U77): "Bus 5" / "Mix 5" (several: "Bus 3,4"). */
export function busBadgeText(meta: Pick<Meta, 'busPrefix'>, numbers: string): string {
  return `${meta.busPrefix === 'mix' ? 'Mix' : 'Bus'} ${numbers}`;
}
